import React, { useEffect, useMemo, useState } from "react";
import CreatePostModal from "../../sections/community/CreatePostModal";
import PostCard from "../../sections/community/PostCard";
import CustomSelect from "../Ui/CustomSelect";
import axios from "axios";
import { useSelector } from "react-redux";
import PrayIcon from "../../asset/icons8-pray-96.png";
import MiracleIcon from "../../asset/icons8-magic-wand-96.png";

const PAGE_SIZE = 10;

export default function CommunityList({ 
  type, 
  title, 
  subtitle, 
  createButtonText, 
  emptyStateEmoji, 
  emptyStateMessage, 
  emptyStateButtonText 
}) {
  const userDetails = useSelector((state) => state.user.user);
  const isAdmin = userDetails?.role === 'admin';

  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [range, setRange] = useState("all");
  const [page, setPage] = useState(1);
  const [openCreate, setOpenCreate] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);



  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      localStorage.getItem("theme") || "light"
    );
  }, []);

  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();

    async function fetchFeed() {
      setLoading(true);
      setErr("");
      const params = new URLSearchParams({
        type,
        page: String(page),
        pageSize: String(PAGE_SIZE),
        q: q.trim(),
        sort,
        range,
      });
      try {
        const res = await axios.get(`/api/community/posts?${params.toString()}`, {
          withCredentials: true,
          signal: controller.signal,
        });
        const data = res?.data || {};
        if (!cancel) {
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancel) setErr("");
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    fetchFeed();

    return () => {
      cancel = true;
      controller.abort();
    };
  }, [q, sort, range, page, type]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleCreateClick = () => {
    if (userDetails.id) {
      setOpenCreate(true);
    } else {
      setShowLoginDialog(true);
    }
  };

  const handleViewConversationsClick = () => {
    if (userDetails.id) {
      window.location.href = "/me/submissions";
    } else {
      setShowLoginDialog(true);
    }
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-start gap-3 mb-2">
            <img 
              src={type === "PRAYER" ? PrayIcon : MiracleIcon} 
              alt={type === "PRAYER" ? "Prayer" : "Miracle"} 
              className="w-8 h-8 sm:w-10 sm:h-10"
            />
            <h1 className="text-2xl sm:text-3xl font-semibold text-nowrap tracking-tight">{title}</h1>
          </div>
          <p className="text-slate-500 text-xs sm:text-sm ml-11 sm:ml-[3.5rem]">{subtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-wrap-reverse justify-end sm:gap-3">
          {!isAdmin && (
            <button
              onClick={handleViewConversationsClick}
              className="flex items-center gap-2 rounded-lg border border-[#0074B9] px-3 sm:px-4 py-2 text-[#0074B9] hover:bg-[#0074B9] hover:text-white transition-all duration-200 text-xs sm:text-base"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle flex-shrink-0">
                <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
              </svg>
              View my conversations
            </button>
          )}
          <button
            onClick={handleCreateClick}
            className="flex items-center gap-2 rounded-lg bg-[#0074B9] px-3 sm:px-4 py-2 text-white shadow hover:bg-[#005a9c] transition-all duration-200 hover:bg-[#0074B9]/80 text-sm sm:text-base"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus flex-shrink-0">
              <path d="M5 12h14"/>
              <path d="M12 5v14"/>
            </svg>
            {createButtonText}
          </button>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 flex flex-col gap-3 sm:gap-4">
        <div className="relative w-full">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder={`Search ${type.toLowerCase()}s…`}
              className="w-full h-[42px] rounded-lg border border-gray-200 bg-white pl-10 pr-4 text-sm font-medium text-gray-700 shadow-sm placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all duration-200"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <CustomSelect
            value={sort}
            onChange={(value) => {
              setPage(1);
              setSort(value);
            }}
            options={[
              { value: "newest", label: "Newest" },
              { value: "most_commented", label: "Most commented" }
            ]}
            placeholder="Sort by"
            className="sm:w-[140px]"
          />
          <CustomSelect
            value={range}
            onChange={(value) => {
              setPage(1);
              setRange(value);
            }}
            options={[
              { value: "all", label: "All time" },
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" }
            ]}
            placeholder="Time range"
            className="sm:w-[140px]"
          />
        </div>
      </div>

      {/* Success Card */}
      {showSuccessCard && (
        <div className="mt-4 sm:mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Post Submitted Successfully!
              </h3>
              <p className="text-sm text-blue-800">
                Your {type.toLowerCase()} has been submitted and is pending admin approval. 
                It will be visible to the community once approved.
              </p>
            </div>
            <button
              onClick={() => setShowSuccessCard(false)}
              className="flex-shrink-0 text-blue-400 hover:text-blue-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
        {loading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg sm:rounded-2xl border p-3 sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-slate-200" />
                  <div className="h-3 sm:h-4 w-32 sm:w-40 bg-slate-200 rounded" />
                </div>
                <div className="mt-3 h-12 sm:h-16 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {err}
          </div>
        )}

        {!loading && !err && items.length === 0 && (
          <div className="rounded-lg sm:rounded-2xl border bg-white p-6 sm:p-8 text-center">
            <div className="text-xl sm:text-2xl mb-1">{emptyStateEmoji}</div>
            <p className="text-slate-700 text-sm sm:text-base">{emptyStateMessage}</p>
            <button
              onClick={handleCreateClick}
              className="mt-3 sm:mt-4 rounded-lg bg-[#0074B9] px-3 sm:px-4 py-2 text-white hover:bg-[#005a9c] transition-all duration-200 text-sm sm:text-base"
            >
              {emptyStateButtonText}
            </button>
          </div>
        )}

        {!loading && !err && items.map((p) => (
          <PostCard key={p._id || p.id} post={p} />
        ))}
      </div>

      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            className="rounded-lg border px-3 py-1.5 disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="text-sm text-slate-600">
            Page {page} / {totalPages}
          </span>
          <button
            className="rounded-lg border px-3 py-1.5 disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}


        <CreatePostModal
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          defaultType={type}
          onSuccess={() => {
            setOpenCreate(false);
            setPage(1);
            setShowSuccessCard(true);
            // Auto hide success card after 5 seconds
            setTimeout(() => setShowSuccessCard(false), 5000);
          }}
        />

        {/* Login Dialog */}
        {showLoginDialog && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowLoginDialog(false);
              }
            }}
          >
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Login Required</h3>
              <p className="text-gray-600 mb-6">
                You need to be logged in to access your conversations. Please log in to continue.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLoginDialog(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <a
                  href={`/login?callbackUrl=${encodeURIComponent(window.location.pathname)}`}
                  className="flex-1 px-4 py-2 bg-[#0074B9] text-white rounded-lg hover:bg-[#005a9c] transition-colors duration-200 text-center"
                >
                  Login
                </a>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
